import dotPropImmutable from 'dot-prop-immutable';
import isEmpty from 'lodash/isEmpty';
import { MemberType } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { TemplateObjectType, TemplateState } from 'data/templates/api';
import { isPrereleaseFeatureEnabled, PrereleaseFeature } from 'hooks/use-feature';
import { queryClient } from 'queries';
import { ROLES_QUERY_KEY } from 'queries/roles';
import { getPlaceholdersFromTasks, getPlaceholdersFromTemplate } from 'routes/projects/create/project-info-step-utils';
import { floatUid } from 'utils/uid';
import { normalizeTemplatePayload } from '../api/transform';
const trimDeletedFormTasks = (projectPhases)=>{
    const removedTaskList = [];
    const updatedProjectPhases = projectPhases.map((phase)=>{
        return {
            ...phase,
            tasks: phase.tasks.reduce((acc, task)=>{
                if (task.formTemplateId && task.formTemplateDeleted) {
                    removedTaskList.push(task.taskId);
                    return acc;
                }
                acc.push(task);
                return acc;
            }, [])
        };
    });
    if (!removedTaskList.length) {
        return updatedProjectPhases;
    }
    // remove the deleted form task from the dependentTask as well
    return updatedProjectPhases.map((phase)=>{
        return {
            ...phase,
            tasks: phase.tasks.map((task)=>{
                return {
                    ...task,
                    dependentTasks: (task.dependentTasks || []).filter((depTask)=>!removedTaskList.includes(depTask.taskId))
                };
            })
        };
    });
};
const removeInvalidPlaceholdersFromTasks = (projectPhases, placeholderLookup = {})=>{
    const updateTask = (task)=>{
        var _ref, _ref1, _ref2;
        var _task_assignee, _task_assignee1;
        return {
            ...task,
            assignee: {
                ...task === null || task === void 0 ? void 0 : task.assignee,
                users: (_ref = task === null || task === void 0 ? void 0 : (_task_assignee = task.assignee) === null || _task_assignee === void 0 ? void 0 : _task_assignee.users) !== null && _ref !== void 0 ? _ref : [],
                teams: ((_ref1 = task === null || task === void 0 ? void 0 : (_task_assignee1 = task.assignee) === null || _task_assignee1 === void 0 ? void 0 : _task_assignee1.teams) !== null && _ref1 !== void 0 ? _ref1 : []).filter((team)=>team.roleId === -1 || team.teamType === MemberType.External || placeholderLookup[team.teamId] != null)
            },
            subTasks: ((_ref2 = task === null || task === void 0 ? void 0 : task.subTasks) !== null && _ref2 !== void 0 ? _ref2 : []).map(updateTask)
        };
    };
    return projectPhases.map((projectPhase)=>{
        var _ref;
        return {
            ...projectPhase,
            tasks: ((_ref = projectPhase === null || projectPhase === void 0 ? void 0 : projectPhase.tasks) !== null && _ref !== void 0 ? _ref : []).map(updateTask)
        };
    });
};
export const templateSlice = {
    fetchProjectTemplateStart: (state, { payload })=>{
        return dotPropImmutable.set(state, `data.${payload.templateId}.loading`, true);
    },
    changeTemplateSaveState: (state, { payload })=>{
        return dotPropImmutable.set(state, `data.${payload.template.templateId}.isSaving`, payload.state);
    },
    fetchProjectTemplateStartSuccess: (state, { payload })=>{
        var _ref, _ref1;
        var _state_data_templateId;
        const { template, rolesWithPlaceholder, allRoles, teamsLookup } = payload;
        const { templateContent: { projectPhases } } = template;
        const sortedProjectPhases = [
            ...projectPhases
        ].sort((projectPhaseA, projectPhasesB)=>projectPhaseA.startDay - projectPhasesB.startDay);
        const normalizeProjectPhases = trimDeletedFormTasks(sortedProjectPhases);
        const placeholders = getPlaceholdersFromTemplate({
            template,
            rolesWithPlaceholder,
            teamsLookup
        });
        const payloadWithSortedProjectPhases = {
            ...template,
            templateContent: {
                ...template.templateContent,
                projectPhases: removeInvalidPlaceholdersFromTasks(normalizeProjectPhases, placeholders)
            }
        };
        const { allocations, allocatedMembers } = payloadWithSortedProjectPhases.templateContent;
        const updatedAllocatedMembers = {
            users: (_ref = allocatedMembers === null || allocatedMembers === void 0 ? void 0 : allocatedMembers.users) !== null && _ref !== void 0 ? _ref : [],
            teams: ((_ref1 = allocatedMembers === null || allocatedMembers === void 0 ? void 0 : allocatedMembers.teams) !== null && _ref1 !== void 0 ? _ref1 : []).filter((team)=>!!(allRoles === null || allRoles === void 0 ? void 0 : allRoles.find((x)=>x.roleId === team.roleId)))
        };
        const allocationMap = (allocations !== null && allocations !== void 0 ? allocations : []).reduce((result, x)=>{
            var _x_billable;
            const allocation = {
                ...x,
                billable: (_x_billable = x.billable) !== null && _x_billable !== void 0 ? _x_billable : true,
                type: x.userId != null ? ResourceType.User : ResourceType.Team
            };
            if (allocation.type === ResourceType.Team && !updatedAllocatedMembers.teams.find((team)=>allocation.teamId === team.teamId)) {
                return result;
            }
            result[x.id] = allocation;
            return result;
        }, {});
        const { entities } = normalizeTemplatePayload(payloadWithSortedProjectPhases);
        const { templateId } = payloadWithSortedProjectPhases;
        const currentSpaceSetup = (_state_data_templateId = state.data[templateId]) === null || _state_data_templateId === void 0 ? void 0 : _state_data_templateId.spaceSetup;
        const result = dotPropImmutable.merge(state, `data.${templateId}`, {
            template: entities.template[templateId],
            projectPhases: entities.projectPhases || {},
            tasks: entities.tasks || {},
            spaceList: entities.spaceList || {},
            sprints: entities.sprints || {},
            placeholders,
            loading: false,
            allocations: allocationMap,
            allocatedMembers: updatedAllocatedMembers,
            spaceSetup: currentSpaceSetup === 'COMPLETE' ? 'COMPLETE' : isEmpty(payloadWithSortedProjectPhases.templateContent.spaceList) ? 'YET_TO_START' : 'COMPLETE'
        });
        return {
            ...result,
            activeId: templateId
        };
    },
    createProjectTemplate: (state, { payload: { templateName, templateContent, isFromIdb, templateMeta, effortPlanning, folderId } })=>{
        var _getPlaceholdersFromTasks, _entities_tasks, _ref, _ref1;
        const roles = queryClient.getQueryData(ROLES_QUERY_KEY);
        const templateId = 'new';
        const emptyPhase = {
            projectPhaseId: floatUid(),
            projectPhaseName: '',
            isNew: true,
            entityOrder: [],
            private: false,
            startDay: 1,
            duration: 1,
            tasks: [],
            sprints: []
        };
        const emptyTemplateContent = {
            duration: 0,
            projectPhases: [
                emptyPhase
            ],
            private: false,
            startDay: 0,
            spaceList: []
        };
        const { entities } = normalizeTemplatePayload({
            templateId,
            templateContent: templateContent !== null && templateContent !== void 0 ? templateContent : emptyTemplateContent,
            templateName,
            objectType: TemplateObjectType.Project,
            state: TemplateState.Published,
            templateMeta,
            effortPlanning,
            ...folderId != null ? {
                folderId
            } : {}
        });
        const result = dotPropImmutable.merge(state, `data.${templateId}`, {
            error: null,
            template: entities.template[templateId],
            projectPhases: entities.projectPhases || {},
            tasks: entities.tasks || {},
            sprints: entities.sprints || {},
            spaceList: entities.spaceList || {},
            loading: false,
            // Agent: empty spaceList stays YET_TO_START (no bounce to /templates).
            // FF off: restore pre-agent isFromIdb COMPLETE quirk for zero impact.
            spaceSetup: isPrereleaseFeatureEnabled(PrereleaseFeature.TemplateAgentChat, true) ? isEmpty(templateContent === null || templateContent === void 0 ? void 0 : templateContent.spaceList) ? 'YET_TO_START' : 'COMPLETE' : isFromIdb && isEmpty(templateContent === null || templateContent === void 0 ? void 0 : templateContent.spaceList) ? 'COMPLETE' : 'YET_TO_START',
            dynamicConditions: {},
            placeholders: (_getPlaceholdersFromTasks = getPlaceholdersFromTasks({
                tasks: Object.values((_entities_tasks = entities.tasks) !== null && _entities_tasks !== void 0 ? _entities_tasks : {}),
                rolesWithPlaceholder: roles,
                teamsLookup: {}
            })) !== null && _getPlaceholdersFromTasks !== void 0 ? _getPlaceholdersFromTasks : {},
            allocations: (_ref = templateContent === null || templateContent === void 0 ? void 0 : templateContent.allocations) !== null && _ref !== void 0 ? _ref : {},
            allocatedMembers: (_ref1 = templateContent === null || templateContent === void 0 ? void 0 : templateContent.allocatedMembers) !== null && _ref1 !== void 0 ? _ref1 : {
                users: [],
                teams: []
            }
        });
        return {
            ...result,
            activeId: templateId,
            activeRecord: undefined
        };
    },
    toggleProjectToTemplate: (state, { payload: { isProjectToTemplate } })=>{
        return {
            ...state,
            projectToTemplate: isProjectToTemplate
        };
    },
    updateProjectTemplate: (state, { payload })=>{
        return dotPropImmutable.set(state, `data.${payload.templateId}.template`, payload);
    },
    updateSpacesList: (state, { payload })=>{
        return dotPropImmutable.set(state, `data.${payload.templateId}.spaceList`, payload.spaceList);
    },
    updateSpaceListOrder: (state, { payload })=>{
        return dotPropImmutable.set(state, `data.${payload.templateId}.template.templateContent.spaceList`, payload.spaceListOrder);
    },
    updateSpaceOrder: (state, { payload })=>{
        return dotPropImmutable.set(state, `data.${payload.templateId}.template.templateContent.spaceOrder`, payload.spaceOrder);
    },
    clearProjectTemplate: (state, { payload })=>{
        return {
            ...state,
            activeId: 'new',
            ...dotPropImmutable.delete(state, `data.${payload.templateId}`)
        };
    },
    setActiveTemplateId: (state, { payload: { templateId } })=>({
            ...state,
            activeId: templateId
        }),
    saveTemplatePlaceholder: (state, { payload })=>{
        const templateData = state.data[state.activeId];
        return {
            ...state,
            data: {
                ...state.data,
                [state.activeId]: {
                    ...templateData,
                    placeholders: {
                        ...templateData.placeholders,
                        [payload.teamId]: payload
                    }
                }
            }
        };
    }
};
